import { useSignal } from "@preact/signals";
import type { LoaderArgs, PageArgs } from "bunfx";
import { navigateTo, RedirectError } from "bunfx";
import { IcoUser, IcoUsers } from "../../components/icons";
import { AppLayout } from "../../components/layout";
import {
  Button,
  Form,
  type FormErrors,
  Input,
  Password,
} from "../../components/ui";
import { usePageTitle } from "../../hooks";
import { rpc } from "../../rpc";
import { showError } from "../../stores/errors";
import type { MimicState } from "../../stores/mimic";
import type { User } from "../../types";
import { getErrorMessage } from "../../utils/errors";

type AcceptState = {
  currentUser: User;
  mimic: MimicState;
  orgId: string;
  orgName: string;
  orgLogoUrl: string | null;
  inviterName: string | null;
  inviterEmail: string | null;
  needsSetup: boolean;
};

export async function load(args: LoaderArgs): Promise<AcceptState> {
  const orgId = args.params.orgId ?? "";

  const authResult = await rpc.auth.me({});

  if (!authResult.user) {
    // Not logged in - redirect to login with return URL
    throw new RedirectError(
      `/login?redirectto=${encodeURIComponent(`/orgs/${orgId}/accept`)}`,
    );
  }

  // Check if user is already a member
  const orgsResult = await rpc.orgs.list({});
  const existingOrg = orgsResult.organizations.find((o) => o.id === orgId);
  if (existingOrg) {
    // Already a member - redirect to home
    throw new RedirectError("/");
  }

  // Get pending invite info
  const inviteResult = await rpc.orgs.getPendingInvite({ orgId });
  if (!inviteResult.invite) {
    // No pending invite - redirect to home
    throw new RedirectError("/");
  }

  // Check if user needs profile setup (no name set, or name is just email prefix)
  const needsSetup =
    !authResult.user.name ||
    authResult.user.name === authResult.user.email.split("@")[0];

  return {
    currentUser: authResult.user as User,
    mimic: authResult.mimic ?? null,
    orgId,
    orgName: inviteResult.invite.orgName,
    orgLogoUrl: inviteResult.invite.orgLogoUrl ?? null,
    inviterName: inviteResult.invite.inviterName,
    inviterEmail: inviteResult.invite.inviterEmail,
    needsSetup,
  };
}

export function Page({ state }: PageArgs<typeof load>) {
  usePageTitle("Accept Invite");
  const {
    currentUser,
    mimic,
    orgId,
    orgName,
    orgLogoUrl,
    inviterName,
    inviterEmail,
    needsSetup,
  } = state.value;
  const isSubmitting = useSignal(false);
  const errors = useSignal({}) as FormErrors;

  const inviterDisplay = inviterName || inviterEmail;

  async function handleAccept(e: Event) {
    e.preventDefault();
    if (isSubmitting.value) return;

    const form = e.target as HTMLFormElement;
    const formData = new FormData(form);

    const name = needsSetup
      ? (formData.get("name") as string)?.trim()
      : undefined;
    const password = needsSetup
      ? (formData.get("password") as string)
      : undefined;

    if (needsSetup && !name) {
      errors.value = { name: "Name is required" };
      return;
    }

    isSubmitting.value = true;
    errors.value = {};

    try {
      await rpc.auth.acceptInvite({
        orgId,
        name: name || undefined,
        password: password || undefined,
      });
      // Store in localStorage so home page shows this org
      localStorage.setItem("pulse:orgId", orgId);
      navigateTo("/");
    } catch (err) {
      showError(getErrorMessage(err));
    } finally {
      isSubmitting.value = false;
    }
  }

  return (
    <AppLayout user={currentUser} mimic={mimic}>
      <div class="flex justify-center" data-brand="blue">
        <div class="w-full max-w-md flex flex-col gap-6">
          {/* Invite Header */}
          <div class="flex flex-col items-center text-center gap-4">
            {inviterDisplay && (
              <div class="flex items-center gap-3">
                <div class="size-10 rounded-full flex items-center justify-center bg-surface-muted">
                  <IcoUser class="size-5 text-text-muted" />
                </div>
                <span class="text-text-muted">
                  <span class="font-medium text-text">{inviterDisplay}</span>{" "}
                  invited you to join
                </span>
              </div>
            )}
            <div class="flex items-center gap-4">
              {orgLogoUrl ? (
                <img
                  src={orgLogoUrl}
                  alt={orgName}
                  class="size-16 rounded-xl object-cover"
                />
              ) : (
                <div class="size-16 rounded-xl flex items-center justify-center bg-surface-muted">
                  <IcoUsers class="size-8 text-text-muted" />
                </div>
              )}
              <h1 class="text-2xl font-bold text-text">{orgName}</h1>
            </div>
          </div>

          {/* Setup Form or Accept Button */}
          <Form
            errors={errors}
            onSubmit={handleAccept}
            class="flex flex-col gap-4"
          >
            {needsSetup && (
              <>
                <p class="text-text-muted text-center">
                  Complete your profile to continue.
                </p>
                <Input
                  label="Your Name"
                  name="name"
                  placeholder="Mx. Fluffington"
                  defaultValue={currentUser.name || ""}
                  autofocus
                  required
                />

                <Password
                  label="Set Password (optional)"
                  name="password"
                  placeholder="Leave blank to use magic links only"
                  autoComplete="new-password"
                />
              </>
            )}

            <Button
              type="submit"
              variant="primary"
              class="w-full"
              disabled={isSubmitting.value}
            >
              {isSubmitting.value ? "Joining..." : "Accept Invitation"}
            </Button>
          </Form>
        </div>
      </div>
    </AppLayout>
  );
}
